import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import {
  NotificationService,
  Notification,
  PaginatedNotificationsRequest,
  PaginatedNotificationsResponse,
} from '@services/notification/notification.service';
import { getNotificationsToAppend } from '@services/notification/notification.utils';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

interface NotificationPaginationState {
  notifications: Notification[];
  nextPage: number;
  totalCount: number;
  isLoading: boolean;
  loadError: boolean;
  hasLoaded: boolean;
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css'],
})
export class NotificationsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('scrollSentinel') scrollSentinel: ElementRef;

  public readonly pageSize: number = 50;
  public isListViewAll: boolean = false;
  locale: string;

  public unreadState: NotificationPaginationState = this.createInitialState();
  public allState: NotificationPaginationState = this.createInitialState();

  private observer: IntersectionObserver;
  private languageSubscription: Subscription;
  private unreadRequest: Subscription;
  private allRequest: Subscription;
  private requestGeneration: number = 0;
  private hasSuccessfullyLoaded: boolean = false;
  private hasMarkedAsReadOnExit: boolean = false;
  private continuationHandler: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public auth: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private translate: TranslateService,
  ) {
    this.locale = this.translate.currentLang;
    this.languageSubscription = this.translate.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        this.locale = event.lang;
        this.resetPagination();
      },
    );
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.languageSubscription?.unsubscribe();
    this.unreadRequest?.unsubscribe();
    this.allRequest?.unsubscribe();
    if (this.continuationHandler !== null) {
      clearTimeout(this.continuationHandler);
      this.continuationHandler = null;
    }
  }

  ngOnInit(): void {
    this.loadNextPage(false);
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage(this.isListViewAll);
        }
      },
      { rootMargin: '200px 0px' },
    );
    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  public get activeState(): NotificationPaginationState {
    return this.isListViewAll ? this.allState : this.unreadState;
  }

  public get notificationsList(): Notification[] {
    return this.activeState.notifications;
  }

  loadNextPage(showAll: boolean = this.isListViewAll): void {
    const state = showAll ? this.allState : this.unreadState;
    if (
      state.isLoading ||
      state.loadError ||
      (state.hasLoaded && state.notifications.length >= state.totalCount)
    ) {
      return;
    }

    state.isLoading = true;
    state.loadError = false;
    const generation = this.requestGeneration;
    const requestedPage = state.nextPage;
    const requestParams: PaginatedNotificationsRequest = {
      language: this.locale,
      page: requestedPage,
      page_size: this.pageSize,
    };
    if (!showAll) {
      requestParams.isRead = false;
    }

    const pageRequest = this.notificationService
      .getNotificationsPage(requestParams)
      .subscribe(
        (response: PaginatedNotificationsResponse) => {
          if (generation !== this.requestGeneration) {
            return;
          }

          const newNotifications = getNotificationsToAppend(
            state.notifications,
            response.data,
          );

          state.notifications = [...state.notifications, ...newNotifications];
          state.totalCount = response.total_count;
          state.hasLoaded = true;
          state.isLoading = false;
          this.hasSuccessfullyLoaded = true;

          if (
            response.data.length > 0 &&
            (newNotifications.length > 0 ||
              state.notifications.length >= state.totalCount)
          ) {
            state.nextPage = requestedPage + 1;
          } else if (state.notifications.length < state.totalCount) {
            state.loadError = true;
          }

          this.continueIfSentinelVisible(showAll);
        },
        () => {
          if (generation !== this.requestGeneration) {
            return;
          }
          state.isLoading = false;
          state.loadError = true;
        },
      );

    if (showAll) {
      this.allRequest = pageRequest;
    } else {
      this.unreadRequest = pageRequest;
    }
  }

  goBack(): void {
    this.router.navigate(['dashboard']);
  }

  togglerListView(showAll: boolean) {
    this.isListViewAll = showAll;
    if (!this.activeState.hasLoaded && !this.activeState.isLoading) {
      this.loadNextPage(showAll);
    } else {
      this.continueIfSentinelVisible(showAll);
    }
  }

  retryActivePage(): void {
    this.activeState.loadError = false;
    this.loadNextPage(this.isListViewAll);
  }

  markNotificationsAsReadOnExit(): Observable<boolean> {
    if (!this.hasSuccessfullyLoaded || this.hasMarkedAsReadOnExit) {
      return of(true);
    }

    this.hasMarkedAsReadOnExit = true;
    return this.notificationService.markAllAsRead('').pipe(
      timeout(4000),
      map(() => true),
      catchError(() => of(true)),
    );
  }

  private resetPagination(): void {
    this.requestGeneration++;
    this.unreadRequest?.unsubscribe();
    this.allRequest?.unsubscribe();
    if (this.continuationHandler !== null) {
      clearTimeout(this.continuationHandler);
      this.continuationHandler = null;
    }
    this.unreadState = this.createInitialState();
    this.allState = this.createInitialState();
    this.isListViewAll = false;
    this.loadNextPage(false);
  }

  private continueIfSentinelVisible(showAll: boolean): void {
    if (showAll !== this.isListViewAll || !this.scrollSentinel) {
      return;
    }

    if (this.continuationHandler !== null) {
      clearTimeout(this.continuationHandler);
    }
    this.continuationHandler = setTimeout(
      () => {
        this.continuationHandler = null;

        if (showAll !== this.isListViewAll || !this.scrollSentinel) {
          return;
        }

        const bounds = this.scrollSentinel.nativeElement.getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        if (bounds.top <= viewportHeight + 200 && bounds.bottom >= -200) {
          this.loadNextPage(showAll);
        }
      },
    ) as unknown as ReturnType<typeof setTimeout>;
  }

  private createInitialState(): NotificationPaginationState {
    return {
      notifications: [],
      nextPage: 1,
      totalCount: 0,
      isLoading: false,
      loadError: false,
      hasLoaded: false,
    };
  }
}
